import { findZoneForPoint, type DeliveryZone, type LatLng } from '@agos/shared'

/**
 * The zone a resolved address falls in — the owner's request of 2026-08-03, *"zóna by sa mala vybrať
 * automaticky podľa adresy"*, and unblocked rather than newly possible.
 *
 * **Everything this needs was already written.** `findZoneForPoint` has been in `@agos/shared` with
 * sixteen tests since the zones were ported, and `delivery-fee.ts` said so in its own note: *"when
 * the key exists, the picker becomes the fallback rather than the only path."* What was missing was
 * a point — `/geocode/search` answered *"Google Maps API key not configured"* until 2026-08-03, so
 * nothing could turn an address into coordinates. The key arrived, the checkout's `AddressField`
 * now hands back `lat`/`lon` when a suggestion is picked, and this is the last link.
 *
 * **It suggests, it does not decide.** The returned zone is applied only when the dispatcher has
 * not already chosen one, because the picker is the deliberate default and the reason survives the
 * key: the zones are named after the villages the caller is already naming, and a person who has
 * chosen *Šurany (4)* did so knowing something the polygon does not. Overwriting that would be the
 * screen arguing with the person using it.
 *
 * `findZoneForPoint` takes the **first** enclosing zone rather than the nearest or smallest, which
 * makes the zone editor's ordering a pricing decision. That is ported behaviour and is not
 * re-decided here.
 */
export function zoneForResolvedAddress(
  place: { lat: number; lon: number } | null | undefined,
  payload: unknown,
  chosenZoneId: string | null,
): string | null {
  if (chosenZoneId !== null) return chosenZoneId
  if (!place || !Number.isFinite(place.lat) || !Number.isFinite(place.lon)) return null

  const zones = zonesOf(payload)
  if (zones.length === 0) return null

  const zone = findZoneForPoint({ lat: place.lat, lng: place.lon }, zones)
  return zone === null ? null : zone.id
}

function point(value: unknown): LatLng | undefined {
  const row = value as Record<string, unknown> | null | undefined
  if (!row || typeof row.lat !== 'number' || typeof row.lng !== 'number') return undefined
  if (!Number.isFinite(row.lat) || !Number.isFinite(row.lng)) return undefined
  return { lat: row.lat, lng: row.lng }
}

/**
 * The wire's zones, narrowed to the geometry.
 *
 * Read off `unknown` rather than the generated `DeliveryZone` because that type has **every field
 * optional** — `id?: string`, `center?: Coordinates`, `radius?: number` — while the shared one, and
 * so `findZoneForPoint`, requires them. A cast between the two would compile and would be a claim
 * the payload does not make.
 *
 * The two filters below are `zoneChoicesOf`'s, deliberately: **a zone this suggests has to be one
 * the picker offers.** The picker skips a row with no usable id and a row somebody switched off, so
 * suggesting either would set `zoneId` to something its `<select>` has no option for — the control
 * would go blank and the delivery fee would come out of the fallback, which is worse than
 * suggesting nothing at all.
 *
 * A row whose `type` is neither shape is dropped for the same reason it would never match:
 * `findZoneForPoint` tests for `'circle'` and `'polygon'` and falls through everything else.
 * `color`, `minOrder` and `tiers` are filled with blanks — the geometry search never reads them,
 * and the fee is `feeCentsFor`'s job from the zone the picker holds.
 */
function zonesOf(payload: unknown): DeliveryZone[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    const id = typeof row.id === 'string' && row.id !== '' ? row.id : null
    if (id === null || row.enabled === false) return []
    if (row.type !== 'circle' && row.type !== 'polygon') return []

    return [
      {
        id,
        name: typeof row.name === 'string' ? row.name : id,
        color: '',
        enabled: true,
        type: row.type,
        center: point(row.center),
        radius: typeof row.radius === 'number' ? row.radius : undefined,
        polygon: Array.isArray(row.polygon)
          ? row.polygon.flatMap((corner) => {
              const corners = point(corner)
              return corners === undefined ? [] : [corners]
            })
          : undefined,
        minOrder: 0,
        tiers: [],
      },
    ]
  })
}
